import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PrimaryButton, IconButton, Dialog } from '@beeline.com/bp-design-system';
import { EngagementManagingSourceModel } from '@utmost.co/enterprise-models';
import { useImportersIntl } from './translations/useImportersIntl';
import { ImportForm } from './ImportForm';
import { AllowedImportType, useImporterData } from './utils/useImporterData';
import { getRsiFields } from './getRsiFields';
import { useRowValidation } from './validations/useRowValidation';
import { useTableValidation } from './validations/useTableValidation';
import { ValidationParams } from './validations/impl/validationUtils';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { ReactSpreadsheetImport, type Result, type Meta, type Data } from '@beeline.com/react-spreadsheet-import';
import styled from 'styled-components';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
import { MixpanelEvents } from '../../../integrations/mixpanel/Events/MixpanelEvents';
import {
  PendingChangesAlert,
  type PendingChangesOption
} from './validations/impl/engagementOpenChangeSet/PendingChangesInlineAlert';
import { ENGAGEMENT_ID_URN } from './validations/impl/engagementOpenChangeSet/useValidateTableAgainstEngagementOpenChangeSet';
import { DiscardConfirmationDialog } from './validations/impl/engagementOpenChangeSet/DiscardConfirmationDialog';

interface IProps {
  allowedImportTypes: AllowedImportType[];
  isMultipleSelectFirstAsDefault?: boolean;
  setModal: (show: boolean) => void;
  onImportComplete?: () => void;
  onImportCanceled?: () => void;
  validationParams?: ValidationParams;
}

export const IMPORTER_HELP_DOCS_LINK =
  'https://docs.bpe.beeline.com/beeline-professional/get-started/about-importing-data.html';

export const Importer: React.FC<React.PropsWithChildren<IProps>> = ({
  allowedImportTypes,
  isMultipleSelectFirstAsDefault = false,
  setModal,
  onImportComplete,
  onImportCanceled,
  validationParams
}) => {
  const intl = useImportersIntl();
  const hasOnlyOneImportType = allowedImportTypes?.length === 1;
  const [showRsiPanel, setShowRsiPanel] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [managingSource, setManagingSource] = useState<EngagementManagingSourceModel | undefined>('UTMOST');
  const { requireExistenceDetectionFieldsAreUnique } = useDarkly();
  const { track } = useMixpanel();

  const {
    importType,
    setImportType,
    onImport,
    importColumns,
    urnToFieldMetadataDefinitionMap,
    fieldMetadataValidator,
    hasFieldMetadataForPaths,
    loading
  } = useImporterData(hasOnlyOneImportType || isMultipleSelectFirstAsDefault ? allowedImportTypes?.[0] : undefined);

  const openChangeSetIdsRef = useRef<Set<string>>(new Set());

  const { fieldDefinitions, fieldsForExistenceDetection, fieldsForReferenceIdValidation } = useMemo(
    () =>
      getRsiFields(
        importType?.entityType,
        importColumns,
        urnToFieldMetadataDefinitionMap,
        fieldMetadataValidator,
        requireExistenceDetectionFieldsAreUnique
      ),
    [
      importColumns,
      urnToFieldMetadataDefinitionMap,
      fieldMetadataValidator,
      requireExistenceDetectionFieldsAreUnique,
      importType?.entityType
    ]
  );

  const rowValidation = useRowValidation(
    importType,
    validationParams,
    fieldsForExistenceDetection,
    fieldsForReferenceIdValidation,
    urnToFieldMetadataDefinitionMap,
    fieldMetadataValidator,
    openChangeSetIdsRef
  );

  const tableValidation = useTableValidation(
    importType,
    validationParams,
    fieldsForExistenceDetection,
    fieldsForReferenceIdValidation,
    urnToFieldMetadataDefinitionMap,
    fieldMetadataValidator,
    openChangeSetIdsRef
  );

  const [pendingChangesOption, setPendingChangesOption] = useState<PendingChangesOption | null>(null);
  const [pendingChangesShowValidation, setPendingChangesShowValidation] = useState(false);
  const discardPendingChangesetsRef = useRef(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const [pendingEngagementCount, setPendingEngagementCount] = useState(0);
  const discardConfirmResolveRef = useRef<((confirmed: boolean) => void) | null>(null);

  const footerAlert = useCallback(
    (data: (Data<string> & Meta)[]) => {
      const hasPendingChanges = data.some((row) => row.__errors?.[ENGAGEMENT_ID_URN]?.level === 'warning');
      if (!hasPendingChanges) return null;
      return (
        <PendingChangesAlert
          selectedOption={pendingChangesOption}
          onOptionChange={(option) => {
            setPendingChangesOption(option);
            setPendingChangesShowValidation(false);
          }}
          showValidation={pendingChangesShowValidation}
        />
      );
    },
    [pendingChangesOption, pendingChangesShowValidation]
  );

  const preSubmitHook = useCallback(
    async (rows: (Data<string> & Meta)[]): Promise<(Data<string> & Meta)[] | null> => {
      const rowsWithPendingChanges = rows.filter((row) => row.__errors?.[ENGAGEMENT_ID_URN]?.level === 'warning');

      if (!rowsWithPendingChanges.length) {
        discardPendingChangesetsRef.current = false;
        return rows;
      }

      if (!pendingChangesOption) {
        setPendingChangesShowValidation(true);
        return null;
      }

      if (pendingChangesOption === 'skipPending') {
        discardPendingChangesetsRef.current = false;
        return rows.filter((row) => !row.__errors?.[ENGAGEMENT_ID_URN]);
      }

      // discardAll: show confirmation dialog before proceeding
      setPendingEngagementCount(rowsWithPendingChanges.length);
      setShowDiscardConfirmation(true);

      const confirmed = await new Promise<boolean>((resolve) => {
        discardConfirmResolveRef.current = resolve;
      });

      if (!confirmed) return null;

      discardPendingChangesetsRef.current = true;
      return rows.map((row) => {
        const errors = row.__errors;
        if (!errors || !errors[ENGAGEMENT_ID_URN]) {
          return row;
        }
        const remainingErrors = { ...errors };
        delete remainingErrors[ENGAGEMENT_ID_URN];
        return { ...row, __errors: Object.keys(remainingErrors).length ? remainingErrors : null } as Data<string> &
          Meta;
      });
    },
    [pendingChangesOption]
  );

  const resetPendingChanges = useCallback(() => {
    setPendingChangesOption(null);
    setPendingChangesShowValidation(false);
    discardPendingChangesetsRef.current = false;
  }, []);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardConfirmation(false);
    discardConfirmResolveRef.current?.(true);
  }, []);

  const handleDiscardCancel = useCallback(() => {
    setShowDiscardConfirmation(false);
    discardConfirmResolveRef.current?.(false);
  }, []);

  const onClose = useCallback(() => {
    setModal(false);
    setShowRsiPanel(false);
    setImportType(undefined);
    setComment('');
    resetPendingChanges();
  }, [setModal, setImportType, resetPendingChanges]);

  const onCloseModal = useCallback(() => {
    track(MixpanelEvents.importerEvents.CANCEL);
    onClose();
    onImportCanceled?.();
  }, [onClose, onImportCanceled, track]);

  const handleOnImport = useCallback(
    async (data: Result<string>, file: File) => {
      return onImport(
        data.validData,
        file.name,
        comment,
        managingSource,
        onImportComplete,
        discardPendingChangesetsRef.current
      );
    },
    [comment, managingSource, onImport, onImportComplete]
  );

  const isContinueEnabled = !!(importType && importColumns && hasFieldMetadataForPaths && comment && managingSource);

  const onRestart = useCallback(() => {
    setShowRsiPanel(false);
    if (!hasOnlyOneImportType) {
      setImportType(undefined);
    }
    setComment('');
    resetPendingChanges();
  }, [hasOnlyOneImportType, setImportType, resetPendingChanges]);

  useEffect(() => {
    track(MixpanelEvents.importerEvents.OPEN);
  }, [track]);

  return (
    <>
      {!showRsiPanel && (
        <Dialog
          actions={[
            <PrimaryButton.Contained
              disabled={!isContinueEnabled}
              dataTest="continue-btn"
              key="rsi-importer"
              onClick={() => {
                track(MixpanelEvents.importerEvents.CONTINUE);
                setShowRsiPanel(true);
              }}
            >
              {intl.continue}
            </PrimaryButton.Contained>
          ]}
          dataTest={`importer_dialog_for_${importType?.entityType}`}
          onClose={onCloseModal}
          open={!showRsiPanel}
          show={{ cancelButton: true }}
          subTitle={
            <>
              {intl.selectDataTypeToMatchType}

              <StyledLearnMoreLink
                href={IMPORTER_HELP_DOCS_LINK}
                target="__blank"
                onClick={() => track(MixpanelEvents.importerEvents.HELP)}
              >
                {intl.learnMore}
                <StyledLinkButton iconName="open_in_new" size="x-small" />
              </StyledLearnMoreLink>
            </>
          }
          title={intl.importData}
        >
          <ImportForm
            allowedImportTypes={allowedImportTypes}
            importType={importType}
            managingSource={managingSource}
            onCommentChanged={setComment}
            onImportTypeChanged={setImportType}
            onManagingSourceChanged={setManagingSource}
            loadingImportData={loading}
          />
        </Dialog>
      )}
      <DiscardConfirmationDialog
        open={showDiscardConfirmation}
        engagementCount={pendingEngagementCount}
        onConfirm={handleDiscardConfirm}
        onCancel={handleDiscardCancel}
      />
      {showRsiPanel && importType?.id && (
        <ReactSpreadsheetImport
          allowInvalidSubmit={false}
          autoMapHeaders
          autoMapSelectValues
          entityType={intl[importType.id as keyof typeof intl] as string}
          fields={fieldDefinitions}
          footerAlert={footerAlert}
          hasOnlyOneImportType={hasOnlyOneImportType}
          isNavigationEnabled
          isOpen={showRsiPanel}
          matcherIgnorePrefixes={MATCHER_IGNORE_PREFIXES}
          onClose={onClose}
          onRestart={onRestart}
          onSubmit={handleOnImport}
          onTrackUserEvent={track}
          preSubmitHook={preSubmitHook}
          rowHook={rowValidation}
          tableHook={tableValidation}
        />
      )}
    </>
  );
};

// To help improve matching accuracy, exclude these
// prefixes when matching as they are not relevant.
const MATCHER_IGNORE_PREFIXES = ['Custom Field: '];

const StyledLearnMoreLink = styled.a`
  color: ${({ theme }) => theme.colorPrimary};
  margin-left: 6px;
  text-decoration: none;
`;

const StyledLinkButton = styled(IconButton)`
  && {
    margin-left: 2px;
  }
`;
